import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Tabs,
  Tab,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

const ADDED_COLOR = "#00A63E";
const REMOVED_COLOR = "#BE3018";

const headerCellSx = {
  border: "none",
  fontSize: "12px",
  fontWeight: 700,
  color: "#454545",
  backgroundColor: "#EEF0F1",
  py: 1,
};

const bodyCellSx = {
  border: "none",
  borderBottom: "1px solid #E0E0E0",
  fontSize: "13px",
  fontWeight: 400,
  color: "#454545",
  backgroundColor: "#FFFFFF",
  py: 1.5,
  verticalAlign: "middle",
};

function normalizeRecordStatus(status) {
  return (status ?? "").toString().trim().toLowerCase();
}

function formatRecordStatusLabel(status) {
  const normalized = normalizeRecordStatus(status);
  if (normalized === "added") return "Added";
  if (normalized === "removed" || normalized === "deleted") return "Removed";
  const label = (status ?? "").toString().trim();
  return label || "—";
}

function recordStatusColor(status) {
  const normalized = normalizeRecordStatus(status);
  if (normalized === "added") return ADDED_COLOR;
  if (normalized === "removed" || normalized === "deleted") return REMOVED_COLOR;
  return "#454545";
}

function TableSkeleton() {
  return (
    <TableContainer
      sx={{
        flex: "1 1 0",
        minHeight: 0,
        maxHeight: 400,
        overflowY: "auto",
        pr: 0.5,
      }}
    >
      <Stack spacing={1.2} sx={{ py: 1, pb: MY_PERF_SCROLL_LIST_PB }}>
        {[...Array(5)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={24} />
        ))}
      </Stack>
    </TableContainer>
  );
}

function RecordStatusCell({ status }) {
  const label = formatRecordStatusLabel(status);
  const color = recordStatusColor(status);

  return (
    <Typography
      component="span"
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color,
        textTransform: "capitalize",
      }}
    >
      {label}
    </Typography>
  );
}

export default function HcpHcoAccountChangesCard({
  title = "HCPs / HCOs Added & Removed This Week",
  hcpRows = [],
  hcoRows = [],
  isLoading = false,
}) {
  const [tab, setTab] = useState(0);

  const rows = useMemo(
    () => (tab === 0 ? hcpRows : hcoRows),
    [tab, hcpRows, hcoRows],
  );

  const hasRows = rows.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <CardContent
        sx={{
          p: "24px",
          pb: "0 !important",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
            {title}
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="standard"
            sx={{
              minHeight: 32,
              mb: 1.5,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 32,
                padding: 0,
                mr: 2,
                fontSize: 13,
                fontWeight: 600,
                color: "#2D2D2D",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#D96B04",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#D96B04",
                height: 2,
              },
            }}
          >
            <Tab label="HCPs" />
            <Tab label="HCOs" />
          </Tabs>
        </Box>

        {isLoading ? (
          <TableSkeleton />
        ) : !hasRows ? (
          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#718096" }}>
              No data found
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              width: "100%",
            }}
          >
            <Box sx={{ pb: MY_PERF_SCROLL_LIST_PB }}>
              <Table
                stickyHeader
                size="small"
                sx={{ tableLayout: "fixed", width: "100%" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ ...headerCellSx, textAlign: "left", width: "70%" }}
                    >
                      Account Name
                    </TableCell>
                    <TableCell
                      sx={{ ...headerCellSx, textAlign: "left", width: "30%" }}
                    >
                      Record Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={row.id ?? `acct-${idx}`}>
                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          fontWeight: 600,
                          color: "#2D2D2D",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {row.accountName || "—"}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <RecordStatusCell status={row.recordStatus} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
